export type EmbeddingWord = {
  word: string;
  vector: number[];
};

export type SimilarityMatch<T extends EmbeddingWord> = {
  word: T;
  similarity: number;
};

export function normalizeWord(value: string): string {
  return value.toLowerCase().trim();
}

export function findEmbeddingByWord<T extends EmbeddingWord>(
  value: string,
  embeddings: T[]
): T | undefined {
  const normalized = normalizeWord(value);
  if (!normalized) return undefined;
  return embeddings.find((embedding) => embedding.word === normalized);
}

export function cosineSimilarity(a: number[], b: number[]): number {
  let dot = 0;
  let normA = 0;
  let normB = 0;
  for (let i = 0; i < a.length; i += 1) {
    dot += a[i] * b[i];
    normA += a[i] * a[i];
    normB += b[i] * b[i];
  }
  return dot / (Math.sqrt(normA) * Math.sqrt(normB));
}

export function vectorAdd(a: number[], b: number[]): number[] {
  return a.map((value, index) => value + b[index]);
}

export function vectorSub(a: number[], b: number[]): number[] {
  return a.map((value, index) => value - b[index]);
}

export function findNearestNeighbors<T extends EmbeddingWord>(
  target: T,
  embeddings: T[],
  count: number
): SimilarityMatch<T>[] {
  const similarities = embeddings
    .filter((embedding) => embedding.word !== target.word)
    .map((embedding) => ({
      word: embedding,
      similarity: cosineSimilarity(target.vector, embedding.vector),
    }))
    .sort((left, right) => right.similarity - left.similarity);
  return similarities.slice(0, count);
}

export function findNearestToVector<T extends EmbeddingWord>(
  vector: number[],
  embeddings: T[],
  count: number,
  exclude: string[] = []
): SimilarityMatch<T>[] {
  const blocked = new Set(exclude.map((value) => normalizeWord(value)));
  const similarities = embeddings
    .filter((embedding) => !blocked.has(embedding.word))
    .map((embedding) => ({
      word: embedding,
      similarity: cosineSimilarity(vector, embedding.vector),
    }))
    .sort((left, right) => right.similarity - left.similarity);
  return similarities.slice(0, count);
}

export function computeAnalogyVector(
  wordA: number[],
  wordB: number[],
  wordC: number[]
): number[] {
  return vectorAdd(vectorSub(wordA, wordB), wordC);
}
